import React, { useState } from 'react';
import { TeacherAvatar } from '../../components/TeacherAvatar';
import { useTeacherAvatar } from '../../hooks/useTeacherAvatar';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { DndContext, useDraggable, useDroppable, DragOverlay } from '@dnd-kit/core';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { ArrowLeft, Sparkles, RotateCcw, Star, Target, Info, Zap, Check, X, Equal } from 'lucide-react';
import { useLabProgress } from '../../hooks/useLabProgress';


const MISSIONS = [
  { id: 1, name: 'Simple Addition', description: 'Solve: x + 3 = 7', equation: { left: [{ type: 'var', label: 'x' }, { type: 'num', value: 3 }], right: [{ type: 'num', value: 7 }] }, answer: 4, stars: 1 },
  { id: 2, name: 'Subtraction', description: 'Solve: x - 5 = 10', equation: { left: [{ type: 'var', label: 'x' }, { type: 'num', value: -5 }], right: [{ type: 'num', value: 10 }] }, answer: 15, stars: 1 },
  { id: 3, name: 'Two-step', description: 'Solve: 2x + 4 = 12', equation: { left: [{ type: 'coeff', coeff: 2, label: '2x' }, { type: 'num', value: 4 }], right: [{ type: 'num', value: 12 }] }, answer: 4, stars: 2 },
  { id: 4, name: 'Negative', description: 'Solve: 3x - 9 = 6', equation: { left: [{ type: 'coeff', coeff: 3, label: '3x' }, { type: 'num', value: -9 }], right: [{ type: 'num', value: 6 }] }, answer: 5, stars: 2 },
  { id: 5, name: 'Challenge', description: 'Solve: 4x + 7 = 31', equation: { left: [{ type: 'coeff', coeff: 4, label: '4x' }, { type: 'num', value: 7 }], right: [{ type: 'num', value: 31 }] }, answer: 6, stars: 3 },
  { id: 6, name: 'Fractions', description: 'Solve: x/2 + 3 = 8', equation: { left: [{ type: 'coeff', coeff: 0.5, label: 'x/2' }, { type: 'num', value: 3 }], right: [{ type: 'num', value: 8 }] }, answer: 10, stars: 2 },
  { id: 7, name: 'Both Sides', description: 'Solve: 5x - 2 = 3x + 6', equation: { left: [{ type: 'coeff', coeff: 5, label: '5x' }, { type: 'num', value: -2 }], right: [{ type: 'coeff', coeff: 3, label: '3x' }, { type: 'num', value: 6 }] }, answer: 4, stars: 3 },
  { id: 8, name: 'Large Numbers', description: 'Solve: 7x - 14 = 35', equation: { left: [{ type: 'coeff', coeff: 7, label: '7x' }, { type: 'num', value: -14 }], right: [{ type: 'num', value: 35 }] }, answer: 7, stars: 2 },
  { id: 9, name: 'Double Negative', description: 'Solve: -2x + 10 = 4', equation: { left: [{ type: 'coeff', coeff: -2, label: '-2x' }, { type: 'num', value: 10 }], right: [{ type: 'num', value: 4 }] }, answer: 3, stars: 3 },
  { id: 10, name: 'Master Equation', description: 'Solve: 6x + 5 = 2x + 25', equation: { left: [{ type: 'coeff', coeff: 6, label: '6x' }, { type: 'num', value: 5 }], right: [{ type: 'coeff', coeff: 2, label: '2x' }, { type: 'num', value: 25 }] }, answer: 5, stars: 3 },
];

const OPERATIONS = [
  { id: 'add1', label: '+1', value: 1 }, { id: 'add5', label: '+5', value: 5 },
  { id: 'sub1', label: '-1', value: -1 }, { id: 'sub5', label: '-5', value: -5 },
  { id: 'sub3', label: '-3', value: -3 }, { id: 'sub4', label: '-4', value: -4 },
  { id: 'sub7', label: '-7', value: -7 }, { id: 'sub9', label: '-9', value: -9 },
  { id: 'div2', label: '÷2', value: 'div2' }, { id: 'div3', label: '÷3', value: 'div3' },
  { id: 'div4', label: '÷4', value: 'div4' }, { id: 'add2', label: '+2', value: 2 },
  { id: 'add3', label: '+3', value: 3 }, { id: 'add14', label: '+14', value: 14 },
  { id: 'sub10', label: '-10', value: -10 }, { id: 'div7', label: '÷7', value: 'div7' },
  { id: 'div6', label: '÷6', value: 'div6' }, { id: 'mul2', label: '×2', value: 'mul2' },
];

function ScalePan({ side, items, weight, isBalanced }) {
  const tilt = isBalanced ? 0 : side === 'left' ? (weight > 0 ? 2 : -2) : (weight > 0 ? -2 : 2);
  return (
    <motion.div animate={{ rotate: tilt }} className="flex-1">
      <div className="text-center mb-2">
        <span className="text-xs text-muted-foreground uppercase tracking-wider">{side} side</span>
      </div>
      <div className="h-28 rounded-xl border-2 border-border/50 bg-card/50 flex items-center justify-center gap-2 px-3 relative overflow-hidden">
        {items.map((item, i) => (
          <motion.div key={i} layout className={`px-3 py-2 rounded-lg text-sm font-bold ${
            item.type === 'var' || item.type === 'coeff' ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30' :
            item.value >= 0 ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' :
            'bg-red-500/20 text-red-400 border border-red-500/30'
          }`}>
            {item.type === 'var' ? item.label : item.type === 'coeff' ? item.label : item.value >= 0 ? `+${item.value}` : item.value}
          </motion.div>
        ))}
        {items.length === 0 && <span className="text-muted-foreground/30 text-sm">empty</span>}
      </div>
    </motion.div>
  );
}

export default function AlgebraLab() {
  const { showTeacher, dismiss, resummon, dismissed, avatarId } = useTeacherAvatar('algebra');
  const navigate = useNavigate();
  const { completedMissions, unlockedMissions, currentMission, setCurrentMission, completeMission: saveProgress, xp, loading: progressLoading } = useLabProgress('algebra', 10);
  const [leftItems, setLeftItems] = useState([]);
  const [rightItems, setRightItems] = useState([]);
  const [steps, setSteps] = useState([]);
  const [result, setResult] = useState(null);
  const [userAnswer, setUserAnswer] = useState('');

  const mission = MISSIONS[currentMission];

  const resetMission = () => {
    setLeftItems([...mission.equation.left]);
    setRightItems([...mission.equation.right]);
    setSteps([]);
    setResult(null);
    setUserAnswer('');
  };

  useState(() => { resetMission(); });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => { resetMission(); }, [currentMission]);

  const applyOperation = (op) => {
    const newLeft = [...leftItems];
    const newRight = [...rightItems];
    const step = { op: op.label };

    if (typeof op.value === 'number') {
      // Add the number to both sides
      newLeft.push({ type: 'num', value: op.value });
      newRight.push({ type: 'num', value: op.value });
      step.desc = `Add ${op.label} to both sides`;
    } else if (op.value.startsWith('div')) {
      const divisor = parseInt(op.value.replace('div', ''));
      step.desc = `Divide both sides by ${divisor}`;
    }

    // Simplify: combine numbers on each side
    const simplify = (items) => {
      let numSum = 0;
      let varItems = [];
      items.forEach(item => {
        if (item.type === 'num') numSum += item.value;
        else if (item.type === 'coeff' && typeof op.value === 'string' && op.value.startsWith('div')) {
          const divisor = parseInt(op.value.replace('div', ''));
          if (item.coeff % divisor === 0) {
            const newCoeff = item.coeff / divisor;
            varItems.push(newCoeff === 1 ? { type: 'var', label: 'x' } : { type: 'coeff', coeff: newCoeff, label: `${newCoeff}x` });
          } else {
            varItems.push(item);
          }
        } else {
          varItems.push(item);
        }
      });
      if (typeof op.value === 'string' && op.value.startsWith('div')) {
        const divisor = parseInt(op.value.replace('div', ''));
        numSum = numSum / divisor;
      }
      const result = [...varItems];
      if (numSum !== 0) result.push({ type: 'num', value: numSum });
      return result.length ? result : [{ type: 'num', value: 0 }];
    };

    const simplifiedLeft = simplify(newLeft);
    const simplifiedRight = simplify(newRight);

    setLeftItems(simplifiedLeft);
    setRightItems(simplifiedRight);
    setSteps(prev => [...prev, step]);
  };

  const checkAnswer = () => {
    const ans = parseFloat(userAnswer);
    if (ans === mission.answer) {
      setResult('correct');
      if (!completedMissions.includes(currentMission)) {
        saveProgress(currentMission);
      }
    } else {
      setResult('incorrect');
    }
  };

  // Calculate if balanced
  const leftWeight = leftItems.reduce((s, i) => s + (i.type === 'num' ? i.value : 0), 0);
  const rightWeight = rightItems.reduce((s, i) => s + (i.type === 'num' ? i.value : 0), 0);
  const isBalanced = leftWeight === rightWeight;

  return (
    <div className="min-h-screen bg-background" data-testid="algebra-lab">
      <TeacherAvatar visible={showTeacher} avatarId={avatarId}
        text="Mathematics lab about algebra equations. Solve equations by balancing a visual scale and dragging terms to isolate the variable."
        cacheKey="lab-intro-algebra"
        onClose={dismiss} dismissed={dismissed} onResummon={resummon} />
      <header className="border-b border-border/50 bg-card/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/labs')} data-testid="back-to-labs">
              <ArrowLeft className="w-4 h-4 mr-2" /> Labs
            </Button>
            <div className="h-6 w-px bg-border" />
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-violet-400" />
              <span className="font-semibold">Algebra Balance Lab</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {MISSIONS.map((m, i) => (
              <Badge key={m.id}
                variant={completedMissions.includes(i) ? 'default' : currentMission === i ? 'secondary' : 'outline'}
                className={`cursor-pointer text-xs ${currentMission === i ? 'ring-2 ring-primary/50' : ''}`}
                onClick={() => { setCurrentMission(i); }}
                data-testid={`mission-${m.id}`}
              >
                {completedMissions.includes(i) && <Star className="w-3 h-3 mr-1 fill-yellow-400 text-yellow-400" />}
                M{m.id}
              </Badge>
            ))}
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-6">
        <motion.div key={currentMission} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}
          className="flex items-center gap-3 mb-6 p-3 rounded-xl bg-violet-500/10 border border-violet-500/20">
          <Target className="w-5 h-5 text-violet-400 shrink-0" />
          <div>
            <span className="font-semibold text-sm">{mission.name}:</span>
            <span className="text-sm text-muted-foreground ml-2">{mission.description}</span>
          </div>
          <div className="ml-auto flex gap-1">
            {[...Array(mission.stars)].map((_, i) => (
              <Star key={i} className={`w-4 h-4 ${completedMissions.includes(currentMission) ? 'fill-yellow-400 text-yellow-400' : 'text-muted-foreground/30'}`} />
            ))}
          </div>
        </motion.div>

        {/* Balance Scale */}
        <div className="mb-6">
          <div className="flex items-end gap-4">
            <ScalePan side="left" items={leftItems} weight={leftWeight - rightWeight} isBalanced={isBalanced} />
            <div className="flex flex-col items-center gap-1 pb-4">
              <Equal className="w-8 h-8 text-muted-foreground" />
              <div className={`w-3 h-3 rounded-full ${isBalanced ? 'bg-green-500' : 'bg-yellow-500'}`} />
            </div>
            <ScalePan side="right" items={rightItems} weight={rightWeight - leftWeight} isBalanced={isBalanced} />
          </div>
        </div>

        {/* Operations */}
        <div className="p-4 rounded-xl bg-card border border-border/50 mb-4">
          <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
            <Zap className="w-4 h-4 text-yellow-400" /> Apply to BOTH sides:
          </h4>
          <div className="flex flex-wrap gap-2">
            {OPERATIONS.map(op => (
              <Button key={op.id} variant="outline" size="sm" onClick={() => applyOperation(op)} className="text-xs" data-testid={`op-${op.id}`}>
                {op.label}
              </Button>
            ))}
            <Button variant="ghost" size="sm" onClick={resetMission} className="ml-auto">
              <RotateCcw className="w-3 h-3 mr-1" /> Reset
            </Button>
          </div>
        </div>

        {/* Steps log */}
        {steps.length > 0 && (
          <div className="p-3 rounded-xl bg-muted/30 border border-border/30 mb-4">
            <h4 className="text-xs font-semibold text-muted-foreground mb-2">Steps:</h4>
            <div className="flex flex-wrap gap-2">
              {steps.map((s, i) => (
                <Badge key={i} variant="outline" className="text-xs">{i+1}. {s.desc || s.op}</Badge>
              ))}
            </div>
          </div>
        )}

        {/* Answer input */}
        <div className="flex items-center gap-3 p-4 rounded-xl bg-card border border-border/50">
          <span className="text-sm font-semibold">x =</span>
          <input
            type="number"
            value={userAnswer}
            onChange={e => { setUserAnswer(e.target.value); setResult(null); }}
            className="w-24 px-3 py-2 rounded-lg bg-muted border border-border text-center font-mono"
            placeholder="?"
            data-testid="answer-input"
          />
          <Button onClick={checkAnswer} disabled={!userAnswer} data-testid="check-answer-btn">
            <Check className="w-4 h-4 mr-1" /> Check
          </Button>
          <AnimatePresence>
            {result === 'correct' && (
              <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} className="flex items-center gap-2 text-green-400 text-sm font-medium">
                <Check className="w-4 h-4" /> Correct!
              </motion.div>
            )}
            {result === 'incorrect' && (
              <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} className="flex items-center gap-2 text-red-400 text-sm font-medium">
                <X className="w-4 h-4" /> Try again
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div className="mt-6 p-4 rounded-xl bg-muted/30 border border-border/30">
          <h4 className="text-xs font-semibold flex items-center gap-1 mb-2 text-muted-foreground">
            <Info className="w-3 h-3" /> How It Works
          </h4>
          <p className="text-xs text-muted-foreground leading-relaxed">
            An equation is like a balance scale — both sides must be equal. To solve for x,
            apply the same operation to both sides. Subtract numbers to isolate the variable term,
            then divide to find x. Whatever you do to one side, you must do to the other!
          </p>
        </div>
      </div>
    </div>
  );
}
